import React, { useState, useEffect } from 'react';
import { getAppData, saveAppData } from '../../lib/storage';
import { 
  listBackups, 
  restoreBackup, 
  deleteBackup, 
  createBackup,
  exportDataAsJSON,
  importDataFromJSON
} from '../../services/storage/backupService';
import { 
  getUserDataHistory, 
  restoreHistoryVersion, 
  deleteHistoryVersion,
  type HistoryVersion 
} from '../../services/sync/historyService';
import { useAuth } from '../../hooks/useAuth';

interface BackupInfo {
  key: string;
  timestamp: number;
  createdAt: string;
}

const DataRecovery: React.FC = () => {
  const { user } = useAuth();
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [historyVersions, setHistoryVersions] = useState<HistoryVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  // 加载备份列表和历史版本
  useEffect(() => {
    loadBackups();
    if (user) {
      loadHistoryVersions();
    }
  }, [user]);

  const loadBackups = async () => {
    try {
      const backupList = await listBackups();
      setBackups(backupList);
    } catch (error) {
      console.error('加载备份列表失败:', error);
      setMessage({ type: 'error', text: '加载备份列表失败' });
    }
  };

  const loadHistoryVersions = async () => {
    setLoadingHistory(true);
    try {
      const { versions, error } = await getUserDataHistory();
      if (error) {
        // 如果表不存在，不显示错误（这是正常的）
        if (!error.includes('未配置') && !error.includes('未登录')) {
          console.debug('[Recovery] 历史版本表可能不存在:', error);
        }
        setHistoryVersions([]);
      } else {
        setHistoryVersions(versions);
      }
    } catch (error) {
      console.error('加载历史版本失败:', error);
      setHistoryVersions([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 恢复 Supabase 历史版本
  const handleRestoreHistory = async (versionNumber: number) => {
    if (!confirm('确定要恢复此历史版本吗？当前数据将被覆盖。')) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await restoreHistoryVersion(versionNumber);
      if (error) {
        setMessage({ type: 'error', text: `恢复失败：${error}` });
        return;
      }

      setMessage({ type: 'success', text: '恢复成功！页面将刷新...' });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: `恢复失败：${error.message || '未知错误'}` });
    } finally {
      setLoading(false);
    }
  };

  // 删除 Supabase 历史版本
  const handleDeleteHistory = async (versionNumber: number) => {
    if (!confirm('确定要删除此历史版本吗？')) {
      return;
    }

    try {
      const { error } = await deleteHistoryVersion(versionNumber);
      if (error) {
        setMessage({ type: 'error', text: `删除失败：${error}` });
        return;
      }

      await loadHistoryVersions();
      setMessage({ type: 'success', text: '删除成功' });
    } catch (error: any) {
      setMessage({ type: 'error', text: `删除失败：${error.message || '未知错误'}` });
    }
  };

  // 恢复备份
  const handleRestore = async (backupKey: string) => {
    if (!confirm('确定要恢复此备份吗？当前数据将被覆盖。')) {
      return;
    }

    setLoading(true);
    try {
      const backupData = await restoreBackup(backupKey);
      if (!backupData) {
        setMessage({ type: 'error', text: '恢复失败：备份数据无效' });
        return;
      }

      // 恢复前创建当前数据的备份
      const currentData = await getAppData();
      try {
        await createBackup(currentData);
      } catch (e) {
        console.warn('创建当前数据备份失败:', e);
      }

      await saveAppData(backupData);
      setMessage({ type: 'success', text: '恢复成功！页面将刷新...' });
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: `恢复失败：${error.message || '未知错误'}` });
    } finally {
      setLoading(false);
    }
  };

  // 删除备份
  const handleDelete = async (backupKey: string) => {
    if (!confirm('确定要删除此备份吗？')) {
      return;
    }

    try {
      await deleteBackup(backupKey);
      await loadBackups();
      setMessage({ type: 'success', text: '删除成功' });
    } catch (error: any) {
      setMessage({ type: 'error', text: `删除失败：${error.message || '未知错误'}` });
    }
  };

  // 导出当前数据
  const handleExport = async () => {
    try {
      const data = await getAppData();
      const json = exportDataAsJSON(data);
      
      // 创建下载链接
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookmark_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: '导出成功' });
    } catch (error: any) {
      setMessage({ type: 'error', text: `导出失败：${error.message || '未知错误'}` });
    }
  };

  // 导入数据
  const handleImport = async () => {
    if (!importText.trim()) {
      setMessage({ type: 'error', text: '请输入要导入的 JSON 数据' });
      return;
    }

    if (!confirm('确定要导入此数据吗？当前数据将被覆盖。')) {
      return;
    }

    setLoading(true);
    try {
      const data = importDataFromJSON(importText);
      if (!data) {
        setMessage({ type: 'error', text: '导入失败：JSON 格式无效' });
        return;
      }

      // 导入前创建当前数据的备份
      const currentData = await getAppData();
      try {
        await createBackup(currentData);
      } catch (e) {
        console.warn('创建当前数据备份失败:', e);
      }

      await saveAppData(data);
      setMessage({ type: 'success', text: '导入成功！页面将刷新...' });
      setShowImport(false);
      setImportText('');
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: `导入失败：${error.message || '未知错误'}` });
    } finally {
      setLoading(false);
    }
  };

  // 格式化时间
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* 消息提示 */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
              : message.type === 'error'
              ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* 操作按钮 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">数据恢复工具</h3>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleExport}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            disabled={loading}
          >
            导出当前数据
          </button>
          <button
            onClick={() => setShowImport(!showImport)}
            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            disabled={loading}
          >
            {showImport ? '取消导入' : '导入数据'}
          </button>
          <button
            onClick={loadBackups}
            className="px-4 py-2 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            disabled={loading}
          >
            刷新备份列表
          </button>
        </div>

        {/* 导入区域 */}
        {showImport && (
          <div className="mt-4 space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              粘贴 JSON 数据：
            </label>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-xs"
              placeholder="粘贴 JSON 格式的数据..."
            />
            <button
              onClick={handleImport}
              className="w-full px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              disabled={loading || !importText.trim()}
            >
              {loading ? '导入中...' : '确认导入'}
            </button>
          </div>
        )}
      </div>

      {/* 备份列表 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          自动备份列表 ({backups.length})
        </h3>
        {backups.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">暂无备份</p>
        ) : (
          <div className="space-y-2">
            {backups.map((backup) => (
              <div
                key={backup.key}
                className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatTime(backup.timestamp)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {backup.createdAt}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRestore(backup.key)}
                    className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    恢复
                  </button>
                  <button
                    onClick={() => handleDelete(backup.key)}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Supabase 历史版本列表 */}
      {user && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Supabase 历史版本 ({historyVersions.length})
            </h3>
            <button
              onClick={loadHistoryVersions}
              className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors disabled:opacity-50"
              disabled={loadingHistory}
            >
              {loadingHistory ? '加载中...' : '刷新'}
            </button>
          </div>
          {historyVersions.length === 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {loadingHistory ? '加载中...' : '暂无历史版本'}
              </p>
              {!loadingHistory && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  提示：需要在 Supabase 中创建 user_data_history 表才能使用此功能
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {historyVersions.map((version) => (
                <div
                  key={version.id}
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      版本 {version.version_number}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(new Date(version.created_at).getTime())}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRestoreHistory(version.version_number)}
                      className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50"
                      disabled={loading}
                    >
                      恢复
                    </button>
                    <button
                      onClick={() => handleDeleteHistory(version.version_number)}
                      className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
                      disabled={loading}
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 使用说明 */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">使用说明</h4>
        <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
          <li><strong>本地自动备份</strong>：每次从云端下载数据时，系统会自动创建本地数据备份</li>
          <li><strong>Supabase 历史版本</strong>：如果创建了 user_data_history 表，每次上传数据时会自动保存历史版本</li>
          <li><strong>恢复备份</strong>：点击"恢复"按钮可以将备份数据恢复到当前数据</li>
          <li><strong>导出数据</strong>：导出当前数据为 JSON 文件，可以手动保存</li>
          <li><strong>导入数据</strong>：从 JSON 文件导入数据，可以恢复之前导出的数据</li>
          <li>系统最多保留 10 个本地自动备份，旧的备份会被自动清理</li>
          <li>Supabase 历史版本需要手动创建表，详见 docs/user_data_history_setup.sql</li>
        </ul>
      </div>
    </div>
  );
};

export default DataRecovery;
